/**
 * Calendar API hooks: events (range + single), RSVP / check-in with optimistic updates, comments
 * with visible-only polling, headcount, roster, series/event editor mutations.
 *
 * Offline: RSVP, check-in and new comments keep their optimistic state when the network is down
 * and go through the durable Dexie outbox (src/client/sync/outbox.ts): kept across restarts and
 * re-sent in order when the network is back. Roster marks (editors) still use the in-memory retry
 * queue below. A queued write the server later rejects is rolled back and reported through
 * `onQueuedRejected` ("Ekki vistað").
 *
 * Query keys (shared contract): ['events', from, to], ['event', id], ['event-comments', id],
 * ['event-roster', id], ['event-headcount', id], ['series', id], ['collections'].
 * Ranges follow the server: `from` and `to` are 'YYYY-MM-DD' dates, both inclusive.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type {
  AttendanceOverride,
  CommentDTO,
  CreateCommentRequest,
  DeleteEventResponse,
  EventDTO,
  HeadcountDTO,
  RosterEntryDTO,
  Rsvp,
  RsvpRequest,
  SeriesDTO,
  SplitSeriesRequest,
  PatchEventRequest,
  PatchSeriesRequest,
  UpsertEventRequest,
  UpsertSeriesRequest,
} from '../../shared/dto/events';
import type { MeDTO } from '../../shared/dto/auth';
import type { CollectionDTO } from '../../shared/dto/library';
import { addDays, DAY_MS, parseDateStr, toDateStr } from '../../shared/time';
import { uuid } from '../../shared/ids';
import { enqueue, isQueued, registerOutboxHandler, removeQueued, subscribeOutbox, type EnqueueOutcome } from '../sync/outbox';
import { checkinBody } from '../sync/outboxLogic';
import { api, ApiFailure, isNetworkFailure, serverNow } from './client';
import { fetchCollections } from './library';
import { dropNotification } from './notifications';
import { visibleInterval } from './poll';
import { queryClient as rootQueryClient } from './queryClient';

/* ─── Keys ──────────────────────────────────────────────────────────────── */

export const eventsKey = (from: string, to: string) => ['events', from, to] as const;
export const eventKey = (id: string) => ['event', id] as const;
export const commentsKey = (eventId: string) => ['event-comments', eventId] as const;
export const rosterKey = (eventId: string) => ['event-roster', eventId] as const;
export const headcountKey = (eventId: string) => ['event-headcount', eventId] as const;
export const seriesKey = (id: string) => ['series', id] as const;
export const COLLECTIONS_KEY = ['collections'] as const;

/* ─── Local (not yet confirmed) participation overlays ──────────────────── */

interface LocalRsvp {
  rsvp: Rsvp;
  /** undefined = the user did not touch the note: the server keeps whatever it has. */
  note?: string | null;
  /** Outbox op id of the write carrying this value (a newer tap replaces it). */
  opId: string;
}
const localRsvp = new Map<string, LocalRsvp>();
const localCheckin = new Map<string, number>();
/** User who owns the pending local state (single-user device; a user switch drops it). */
let localOwner: string | null = null;

function currentUserId(): string | null {
  return rootQueryClient.getQueryData<MeDTO | null>(['me'])?.id ?? null;
}

/** Drops pending local state that belongs to another user than the signed-in one. */
function checkOwner(): void {
  const uid = currentUserId();
  if (localOwner !== null && localOwner !== uid) {
    localRsvp.clear();
    localCheckin.clear();
    retryQueue.clear();
    pendingRejections.length = 0;
  }
  localOwner = uid;
}

/** Applies RSVP / check-in that were tapped but not yet confirmed by the server. */
export function applyLocal(ev: EventDTO): EventDTO {
  if (localRsvp.size === 0 && localCheckin.size === 0) return ev;
  checkOwner();
  const r = localRsvp.get(ev.id);
  const c = localCheckin.get(ev.id);
  if (!r && c === undefined) return ev;
  const checkingIn = c !== undefined && ev.my.checkedInAt == null;
  return {
    ...ev,
    my: {
      ...ev.my,
      // Like the server: checking in answers "Kem" for a member who had not answered.
      ...(checkingIn && !r && ev.my.rsvp === null ? { rsvp: 'yes', effectiveRsvp: 'yes' } : {}),
      ...(r ? { rsvp: r.rsvp, effectiveRsvp: r.rsvp } : {}),
      ...(r && r.note !== undefined ? { note: r.note } : {}),
      ...(checkingIn ? { checkedInAt: c } : {}),
    },
  };
}

/** True while an RSVP or check-in for this event is waiting for the network (queued offline). */
export function isParticipationQueued(eventId: string): boolean {
  return isQueued(`rsvp:${eventId}`) || isQueued(`checkin:${eventId}`);
}

/*
 * The outbox lists a write as queued from the moment it is stored, also while its first (online)
 * attempt is still on the way. "Bíður sendingar" must not flash for that ordinary round trip: a tap's
 * key is left out of the marker until its first attempt ended 'queued' (offline: at once) or has
 * taken longer than PENDING_MARKER_GRACE_MS (slow hall Wi-Fi: then it really is waiting).
 */
export const PENDING_MARKER_GRACE_MS = 800;
/** Outbox key → number of taps whose first attempt is still within the grace period. */
const firstAttempts = new Map<string, number>();
const markerListeners = new Set<() => void>();

function notifyMarker(): void {
  for (const fn of markerListeners) fn();
}

/** Holds the marker of `key` back while this tap's first attempt (`outcome`) runs, at most the grace period. */
function holdMarker<T>(key: string, outcome: Promise<T>): Promise<T> {
  // Already shown for an earlier queued tap: it stays (no hide-and-show flicker).
  if (isQueued(key)) return outcome;
  firstAttempts.set(key, (firstAttempts.get(key) ?? 0) + 1);
  let held = true;
  const release = () => {
    if (!held) return;
    held = false;
    clearTimeout(timer);
    const n = (firstAttempts.get(key) ?? 1) - 1;
    if (n > 0) firstAttempts.set(key, n);
    else firstAttempts.delete(key);
    notifyMarker();
  };
  const timer = setTimeout(release, PENDING_MARKER_GRACE_MS);
  outcome.then(release, release);
  return outcome;
}

function shownQueued(key: string): boolean {
  return isQueued(key) && !firstAttempts.has(key);
}

/** isParticipationQueued() as the "Bíður sendingar" marker shows it (see holdMarker). */
export function isParticipationPendingShown(eventId: string): boolean {
  return shownQueued(`rsvp:${eventId}`) || shownQueued(`checkin:${eventId}`);
}

/** Re-renders on outbox changes and when a held-back marker is released. */
export function subscribeParticipationQueued(fn: () => void): () => void {
  const off = subscribeOutbox(fn);
  markerListeners.add(fn);
  return () => {
    off();
    markerListeners.delete(fn);
  };
}

/**
 * The "Bíður sendingar" marker as React state: appears right after an offline tap (or when an
 * online attempt fails or hangs) and goes once the write is sent.
 */
export function useParticipationQueued(eventId: string): boolean {
  const get = useCallback(() => isParticipationPendingShown(eventId), [eventId]);
  return useSyncExternalStore(subscribeParticipationQueued, get, get);
}

export function patchEventEverywhere(qc: QueryClient, id: string, fn: (ev: EventDTO) => EventDTO): void {
  qc.setQueryData<EventDTO>(eventKey(id), (old) => (old ? fn(old) : old));
  qc.setQueriesData<EventDTO[]>({ queryKey: ['events'] }, (list) => (list?.some((e) => e.id === id) ? list.map((e) => (e.id === id ? fn(e) : e)) : list));
}

function isEventDTO(v: unknown, id: string): v is EventDTO {
  return !!v && typeof v === 'object' && (v as EventDTO).id === id && typeof (v as EventDTO).my === 'object';
}

/** Puts a server EventDTO into every cache that holds it; otherwise refetches the event. */
function acceptServerEvent(qc: QueryClient, id: string, res: unknown): void {
  if (isEventDTO(res, id)) {
    const fresh = applyLocal(res);
    qc.setQueryData<EventDTO>(eventKey(id), fresh);
    patchEventEverywhere(qc, id, () => fresh);
  } else {
    void qc.invalidateQueries({ queryKey: eventKey(id) });
    void qc.invalidateQueries({ queryKey: ['events'] });
  }
}

/** After my RSVP / check-in: counts and roster change, the event itself came back in the response. */
function invalidateCounts(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: headcountKey(id) });
  void qc.invalidateQueries({ queryKey: rosterKey(id) });
}

function refetchEvent(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: eventKey(id) });
  void qc.invalidateQueries({ queryKey: ['events'] });
  invalidateCounts(qc, id);
}

/* ─── In-memory retry queue (until the M5 outbox) ───────────────────────── */

const retryQueue = new Map<string, () => Promise<void>>();
let flushing = false;

async function flushRetries(): Promise<void> {
  if (flushing || retryQueue.size === 0) return;
  checkOwner();
  flushing = true;
  try {
    for (const [key, run] of [...retryQueue]) {
      try {
        await run();
        if (retryQueue.get(key) === run) retryQueue.delete(key);
      } catch (e) {
        if (isNetworkFailure(e)) break; // still offline — keep everything, try again later
        if (retryQueue.get(key) === run) retryQueue.delete(key);
      }
    }
  } finally {
    flushing = false;
  }
}

/**
 * Queues `run` to be retried when back online (latest per key wins). `onGiveUp` runs when the server
 * finally rejects it (e.g. the event started meanwhile) so optimistic state can be rolled back.
 */
function queueRetry(key: string, run: () => Promise<void>, onGiveUp?: (e: unknown) => void): void {
  checkOwner();
  retryQueue.set(key, async () => {
    try {
      await run();
    } catch (e) {
      if (!isNetworkFailure(e)) onGiveUp?.(e);
      throw e;
    }
  });
}

/* ─── Rejected queued writes ("Ekki vistað") ────────────────────────────── */

export type QueuedKind = 'rsvp' | 'checkin' | 'comment' | 'roster';
export interface QueuedRejection {
  kind: QueuedKind;
  eventId: string;
  error: unknown;
}

const rejectionListeners: ((r: QueuedRejection) => void)[] = [];
const pendingRejections: QueuedRejection[] = [];

/** Tells the UI that a write queued offline was later rejected by the server (and rolled back). */
function reportRejected(r: QueuedRejection): void {
  const fn = rejectionListeners[rejectionListeners.length - 1];
  if (fn) fn(r);
  else pendingRejections.push(r);
}

/**
 * Subscribes to queued (offline) writes that the server later rejected. Only the most recently
 * subscribed listener is called, so several mounted screens never show the same message twice;
 * rejections that happen while nobody listens are delivered to the next subscriber.
 */
export function onQueuedRejected(fn: (r: QueuedRejection) => void): () => void {
  rejectionListeners.push(fn);
  for (const r of pendingRejections.splice(0)) fn(r);
  return () => {
    const i = rejectionListeners.lastIndexOf(fn);
    if (i >= 0) rejectionListeners.splice(i, 1);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void flushRetries());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) void flushRetries();
  });
  setInterval(() => {
    if (navigator.onLine && document.visibilityState === 'visible') void flushRetries();
  }, 60_000);
}

export type SubmitResult = 'sent' | 'queued';

/* ─── Events ────────────────────────────────────────────────────────────── */

function byStart(a: EventDTO, b: EventDTO): number {
  return a.startsAt - b.startsAt || a.title.localeCompare(b.title, 'is');
}

/** Does the event start within [fromMs, toEndMs)? (Bucketed by start day like the calendar.) */
function inRange(ev: EventDTO, fromMs: number, toEndMs: number): boolean {
  return ev.startsAt >= fromMs && ev.startsAt < toEndMs;
}

/** [from 00:00, to 24:00) in ms for inclusive 'YYYY-MM-DD' dates. */
function rangeMs(from: string, to: string): [number, number] {
  return [parseDateStr(from), parseDateStr(to) + DAY_MS];
}

async function fetchEvents(from: string, to: string): Promise<EventDTO[]> {
  const list = await api<EventDTO[]>(`/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  const [f, t] = rangeMs(from, to);
  return list
    .filter((e) => e.status !== 'deleted' && inRange(e, f, t))
    .map(applyLocal)
    .sort(byStart);
}

/**
 * Builds a list for [from, to] from whatever event ranges are already cached (e.g. persisted
 * from a previous session) so the calendar renders instantly and offline on a new day/month.
 */
function cachedEventsInRange(qc: QueryClient, from: string, to: string): EventDTO[] | undefined {
  const [f, t] = rangeMs(from, to);
  const byId = new Map<string, EventDTO>();
  let covered = false;
  for (const [key, list] of qc.getQueriesData<EventDTO[]>({ queryKey: ['events'] })) {
    if (!list) continue;
    const kf = typeof key[1] === 'string' ? key[1] : null;
    const kt = typeof key[2] === 'string' ? key[2] : null;
    if (kf && kt && kf <= from && kt >= to) covered = true;
    for (const e of list) if (inRange(e, f, t) && !byId.has(e.id)) byId.set(e.id, applyLocal(e));
  }
  if (byId.size === 0 && !covered) return undefined;
  return [...byId.values()].sort(byStart);
}

/** `staleTime`: for readers that only glance at the list (e.g. the practice quick menu) — no refetch on every mount. */
export function useEvents(from: string, to: string, opts: { staleTime?: number } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: eventsKey(from, to),
    queryFn: () => fetchEvents(from, to),
    meta: { persist: true },
    placeholderData: () => cachedEventsInRange(qc, from, to),
    ...(opts.staleTime !== undefined ? { staleTime: opts.staleTime } : {}),
  });
}

/** Warm the cache for an adjacent month so swiping feels instant. */
export function prefetchEvents(qc: QueryClient, from: string, to: string): void {
  void qc.prefetchQuery({ queryKey: eventsKey(from, to), queryFn: () => fetchEvents(from, to), meta: { persist: true }, staleTime: 60_000 });
}

/**
 * Finds the occurrence of a series on a given date (e.g. after "this and following" created a new
 * series) — fetched fresh from the server, not persisted.
 */
export async function findSeriesOccurrence(qc: QueryClient, seriesId: string, date: string): Promise<EventDTO | null> {
  // No retries and no pausing while offline: a NetworkFailure must reject at once (the caller waits on it).
  const list = await qc.fetchQuery({ queryKey: eventsKey(date, date), queryFn: () => fetchEvents(date, date), staleTime: 0, retry: false, networkMode: 'always' });
  return list.find((e) => e.seriesId === seriesId && e.status !== 'deleted') ?? null;
}

/**
 * Is there a not-yet-started, non-detached occurrence of the series before `date`? Decides whether
 * "this and following" can be a PATCH (which changes every future occurrence) or needs a split.
 */
export async function hasUpcomingOccurrenceBefore(qc: QueryClient, seriesId: string, date: string): Promise<boolean> {
  const now = serverNow();
  const today = toDateStr(now);
  if (date <= today) return false;
  const to = addDays(date, -1);
  if ((parseDateStr(to) - parseDateStr(today)) / DAY_MS > 365) return true;
  // No retries and no pausing while offline, so the editor's Save never spins forever without network.
  const list = await qc.fetchQuery({ queryKey: eventsKey(today, to), queryFn: () => fetchEvents(today, to), staleTime: 0, retry: false, networkMode: 'always' });
  return list.some((e) => e.seriesId === seriesId && !e.isDetached && e.startsAt > now && (e.originalDate ?? toDateStr(e.startsAt)) < date);
}

function findCachedEvent(qc: QueryClient, id: string): EventDTO | undefined {
  for (const [, list] of qc.getQueriesData<EventDTO[]>({ queryKey: ['events'] })) {
    const hit = list?.find((e) => e.id === id);
    if (hit) return applyLocal(hit);
  }
  return undefined;
}

/** 404/403 for an event: deleted, or its audience no longer includes me. */
export function isEventGone(e: unknown): boolean {
  return e instanceof ApiFailure && (e.status === 404 || e.status === 403);
}

/**
 * Single event. When the server says it is gone, it is also dropped from every cached list; the
 * stale `['event', id]` data TanStack keeps on error must then not be shown (check isEventGone).
 */
export function useEvent(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: eventKey(id),
    queryFn: async () => {
      try {
        return applyLocal(await api<EventDTO>(`/events/${encodeURIComponent(id)}`));
      } catch (e) {
        if (isEventGone(e)) {
          qc.setQueriesData<EventDTO[]>({ queryKey: ['events'] }, (list) => (list?.some((x) => x.id === id) ? list.filter((x) => x.id !== id) : list));
        }
        throw e;
      }
    },
    meta: { persist: true },
    placeholderData: () => findCachedEvent(qc, id),
  });
}

/* ─── RSVP & check-in ───────────────────────────────────────────────────── */

/*
 * Writes go through the durable outbox (src/client/sync/outbox.ts): stored in IndexedDB, sent at
 * once when online, otherwise kept (also across restarts) and sent in order when the network is back.
 * A newer tap on the same event replaces a queued one but keeps its queue position. The QueryClient
 * of the last submit is used by the outbox handlers (the app has a single client).
 */
let outboxQc: QueryClient = rootQueryClient;

/** 'sent' / 'queued' for the caller; a rejection of the first attempt is rethrown (caller shows it). */
function submitResult(o: EnqueueOutcome, onQueued?: () => void): SubmitResult {
  if (o.status === 'sent') return 'sent';
  if (o.status === 'queued') {
    onQueued?.();
    return 'queued';
  }
  throw o.error;
}

const eventIdOf = (key: string, prefix: string) => key.slice(prefix.length);
const rsvpPath = (eventId: string) => `/events/${encodeURIComponent(eventId)}/rsvp`;
const checkinPath = (eventId: string) => `/events/${encodeURIComponent(eventId)}/checkin`;

/**
 * The outbox was wiped (logout, also a forced one with unsent writes): forget the optimistic state of
 * those writes, so signing in again as the same user without a reload never shows values the server
 * never received.
 */
function resetQueuedState(): void {
  localRsvp.clear();
  localCheckin.clear();
  restoredComments.clear();
  pendingRejections.length = 0;
  localOwner = null;
}

/** A queued RSVP replaced by a newer tap without a note keeps the queued note edit. */
function mergeRsvp(prev: unknown, next: unknown): unknown {
  const p = prev as RsvpRequest;
  const n = next as RsvpRequest;
  return n.note === undefined && p?.note !== undefined ? { ...n, note: p.note } : n;
}

registerOutboxHandler('rsvp', {
  onSent(item, res) {
    const eventId = eventIdOf(item.key, 'rsvp:');
    if (localRsvp.get(eventId)?.opId === item.id) localRsvp.delete(eventId);
    acceptServerEvent(outboxQc, eventId, res);
    invalidateCounts(outboxQc, eventId);
  },
  onRejected(item, error, immediate) {
    const eventId = eventIdOf(item.key, 'rsvp:');
    if (localRsvp.get(eventId)?.opId === item.id) localRsvp.delete(eventId);
    refetchEvent(outboxQc, eventId);
    if (!immediate) reportRejected({ kind: 'rsvp', eventId, error });
  },
  onRestore(item) {
    checkOwner();
    const eventId = eventIdOf(item.key, 'rsvp:');
    const b = item.body as RsvpRequest;
    localRsvp.set(eventId, b.note === undefined ? { rsvp: b.rsvp, opId: item.id } : { rsvp: b.rsvp, note: b.note, opId: item.id });
    patchEventEverywhere(outboxQc, eventId, applyLocal);
  },
  onReset: resetQueuedState,
});

/**
 * Sets my RSVP (optimistic). Resolves 'sent' when the server confirmed, 'queued' when offline
 * (state kept locally and re-sent when back online). Rejects on a real API error (rolled back).
 * Pass `note` only when the user edited it (null clears it); without it the server keeps the note.
 */
export function submitRsvp(eventId: string, req: RsvpRequest, qc: QueryClient = rootQueryClient): Promise<SubmitResult> {
  checkOwner();
  outboxQc = qc;
  const opId = uuid();
  // A newer tap without a note must not drop a note edit that is still waiting to be sent.
  const note = req.note !== undefined ? req.note : localRsvp.get(eventId)?.note;
  localRsvp.set(eventId, note === undefined ? { rsvp: req.rsvp, opId } : { rsvp: req.rsvp, note, opId });
  patchEventEverywhere(qc, eventId, applyLocal);
  // An omitted note means "keep" on the server: only send it when the user actually edited it.
  const body: RsvpRequest = note === undefined ? { rsvp: req.rsvp } : { rsvp: req.rsvp, note };
  // The "queued" marker follows the outbox itself (useParticipationQueued), held back during the first attempt.
  const key = `rsvp:${eventId}`;
  return holdMarker(key, enqueue({ id: opId, kind: 'rsvp', key, method: 'PUT', path: rsvpPath(eventId), body, merge: mergeRsvp })).then((o) => submitResult(o));
}

function clearLocalCheckin(qc: QueryClient, eventId: string): void {
  localCheckin.delete(eventId);
  patchEventEverywhere(qc, eventId, (ev) => ({ ...ev, my: { ...ev.my, checkedInAt: null } }));
  refetchEvent(qc, eventId);
}

registerOutboxHandler('checkin', {
  // Live only on the first attempt right at the tap; queued / retried → estServerTime captured at the
  // tap (server: 'self-offline'), so a retry after the window closed is still accepted.
  prepare(item) {
    return checkinBody(item.attempts, (item.body as { estServerTime: number }).estServerTime, serverNow());
  },
  onSent(item, res) {
    const eventId = eventIdOf(item.key, 'checkin:');
    localCheckin.delete(eventId);
    acceptServerEvent(outboxQc, eventId, res);
    invalidateCounts(outboxQc, eventId);
  },
  onRejected(item, error, immediate) {
    const eventId = eventIdOf(item.key, 'checkin:');
    clearLocalCheckin(outboxQc, eventId);
    if (!immediate) reportRejected({ kind: 'checkin', eventId, error });
  },
  onRestore(item) {
    checkOwner();
    const eventId = eventIdOf(item.key, 'checkin:');
    localCheckin.set(eventId, (item.body as { estServerTime: number }).estServerTime);
    patchEventEverywhere(outboxQc, eventId, applyLocal);
  },
  onReset: resetQueuedState,
});

/** Self check-in (optimistic, see submitRsvp). Offline check-ins are re-sent with estServerTime. */
export function submitCheckin(eventId: string, qc: QueryClient = rootQueryClient): Promise<SubmitResult> {
  checkOwner();
  outboxQc = qc;
  const tappedAt = serverNow();
  // A second tap while the first is queued keeps the first tap's time (the queued body is kept too).
  localCheckin.set(eventId, Math.min(localCheckin.get(eventId) ?? tappedAt, tappedAt));
  patchEventEverywhere(qc, eventId, applyLocal);
  const key = `checkin:${eventId}`;
  return holdMarker(
    key,
    enqueue({
      id: uuid(),
      kind: 'checkin',
      key,
      method: 'POST',
      path: checkinPath(eventId),
      body: { estServerTime: tappedAt },
      merge: (prev) => prev,
    }),
  ).then((o) => submitResult(o));
}

export function useRsvp(eventId: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (req: RsvpRequest) => submitRsvp(eventId, req, qc) });
}

export function useCheckin(eventId: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => submitCheckin(eventId, qc) });
}

/* ─── Headcount & roster ────────────────────────────────────────────────── */

export function useHeadcount(eventId: string, enabled = true) {
  return useQuery({
    queryKey: headcountKey(eventId),
    queryFn: () => api<HeadcountDTO>(`/events/${encodeURIComponent(eventId)}/headcount`),
    enabled,
    staleTime: 15_000,
    gcTime: 30 * 60_000,
  });
}

export function useRoster(eventId: string, enabled = true) {
  return useQuery({
    queryKey: rosterKey(eventId),
    queryFn: () => api<RosterEntryDTO[]>(`/events/${encodeURIComponent(eventId)}/roster`),
    enabled,
    staleTime: 10_000,
    gcTime: 30 * 60_000,
    refetchInterval: visibleInterval(20_000),
    refetchIntervalInBackground: false,
  });
}

/**
 * Roster overrides with latest-wins semantics: every tap updates the UI immediately; requests
 * for the same member are serialised and only the newest value is sent once the previous one
 * finishes. `overrides` holds values not yet confirmed so polling never flickers them back.
 */
export function useRosterOverrides(eventId: string, onError: (e: unknown, queued: boolean) => void) {
  const qc = useQueryClient();
  const [overrides, setOverrides] = useState<ReadonlyMap<string, AttendanceOverride | null>>(() => new Map());
  const desired = useRef(new Map<string, AttendanceOverride | null>());
  const inflight = useRef(new Set<string>());
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  const sync = useCallback(() => setOverrides(new Map(desired.current)), []);

  const pump = useCallback(
    async (userId: string): Promise<void> => {
      if (inflight.current.has(userId) || !desired.current.has(userId)) return;
      const value = desired.current.get(userId) ?? null;
      inflight.current.add(userId);
      try {
        const res = await api<RosterEntryDTO | undefined>(`/events/${encodeURIComponent(eventId)}/roster/${encodeURIComponent(userId)}`, {
          method: 'PUT',
          body: { override: value },
        });
        // A poll that started before this write would bring the old value back: drop it.
        await qc.cancelQueries({ queryKey: rosterKey(eventId) });
        qc.setQueryData<RosterEntryDTO[]>(rosterKey(eventId), (list) =>
          list?.map((r) => (r.userId !== userId ? r : res && res.userId === userId ? res : { ...r, override: value })),
        );
        if (desired.current.get(userId) === value) {
          desired.current.delete(userId);
          sync();
        }
      } catch (e) {
        const queued = isNetworkFailure(e);
        if (queued) {
          const settle = () => {
            if (desired.current.get(userId) === value) {
              desired.current.delete(userId);
              sync();
            }
            void qc.invalidateQueries({ queryKey: rosterKey(eventId) });
          };
          queueRetry(
            `roster:${eventId}:${userId}`,
            async () => {
              await api<unknown>(`/events/${encodeURIComponent(eventId)}/roster/${encodeURIComponent(userId)}`, { method: 'PUT', body: { override: value } });
              settle();
            },
            (err) => {
              settle();
              reportRejected({ kind: 'roster', eventId, error: err });
            },
          );
        } else {
          desired.current.delete(userId);
          sync();
          void qc.invalidateQueries({ queryKey: rosterKey(eventId) });
        }
        onErrorRef.current(e, queued);
        return;
      } finally {
        inflight.current.delete(userId);
      }
      if (desired.current.has(userId)) void pump(userId);
    },
    [eventId, qc, sync],
  );

  const setOverride = useCallback(
    (userId: string, value: AttendanceOverride | null) => {
      desired.current.set(userId, value);
      sync();
      void pump(userId);
    },
    [pump, sync],
  );

  return { overrides, setOverride };
}

/* ─── Comments ──────────────────────────────────────────────────────────── */

/** A comment as kept on the board; `local` marks optimistic entries not yet confirmed. */
export type BoardComment = CommentDTO & { local?: 'sending' | 'queued' | 'failed' };

function byCreated(a: BoardComment, b: BoardComment): number {
  return a.createdAt - b.createdAt || a.id.localeCompare(b.id);
}

/** Merges a server delta into the board list (by id). Pending local edits win over polled rows. */
export function mergeComments(prev: readonly BoardComment[], delta: readonly CommentDTO[]): BoardComment[] {
  const map = new Map<string, BoardComment>();
  for (const c of prev) map.set(c.id, c);
  for (const c of delta) {
    const cur = map.get(c.id);
    // Keep our newer copy (a pending edit/delete, or a row we already have in a newer version).
    // Queued/failed local creates are always replaced: the server has it after all.
    if (cur && (cur.local === 'sending' || !cur.local) && c.updatedAt < cur.updatedAt) continue;
    map.set(c.id, { ...c });
  }
  return [...map.values()].sort(byCreated);
}

function serverWatermark(list: readonly BoardComment[]): number | null {
  let max: number | null = null;
  for (const c of list) if (!c.local && (max === null || c.updatedAt > max)) max = c.updatedAt;
  return max;
}

/**
 * Message board for an event. Polls every 25 s only while the page is visible and the board is
 * mounted, fetching `?since=` deltas (with a 5 s overlap) and merging them by id.
 */
export function useComments(eventId: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: commentsKey(eventId),
    queryFn: async () => {
      const prev = qc.getQueryData<BoardComment[]>(commentsKey(eventId));
      const mark = prev ? serverWatermark(prev) : null;
      const qs = mark === null ? '' : `?since=${Math.max(0, mark - 5000)}`;
      const delta = await api<CommentDTO[]>(`/events/${encodeURIComponent(eventId)}/comments${qs}`);
      // Re-read: optimistic changes may have happened while the request was in flight.
      return mergeComments(qc.getQueryData<BoardComment[]>(commentsKey(eventId)) ?? [], delta);
    },
    staleTime: 10_000,
    gcTime: 30 * 60_000,
    refetchInterval: visibleInterval(25_000),
    refetchIntervalInBackground: false,
  });
}

function setComment(qc: QueryClient, eventId: string, id: string, fn: (c: BoardComment) => BoardComment | null): void {
  qc.setQueryData<BoardComment[]>(commentsKey(eventId), (list) => {
    if (!list) return list;
    const out: BoardComment[] = [];
    for (const c of list) {
      if (c.id !== id) out.push(c);
      else {
        const next = fn(c);
        if (next) out.push(next);
      }
    }
    return out;
  });
}

function acceptServerComment(qc: QueryClient, eventId: string, id: string, res: unknown): void {
  if (res && typeof res === 'object' && (res as CommentDTO).id === id && 'author' in res) {
    setComment(qc, eventId, id, () => ({ ...(res as CommentDTO) }));
  } else {
    setComment(qc, eventId, id, (c) => ({ ...c, local: undefined }));
  }
}

/** Outbox body of a new comment (the request plus what the board needs to show it after a restart). */
interface QueuedComment {
  eventId: string;
  req: CreateCommentRequest;
  author: CommentDTO['author'];
  createdAt: number;
}

/** Comments still queued after a restart, put back on their board (marked "queued") when it loads. */
const restoredComments = new Map<string, BoardComment>();
let watchingBoards = false;

function injectRestored(qc: QueryClient, eventId: string): void {
  const list = qc.getQueryData<BoardComment[]>(commentsKey(eventId));
  if (!list) return;
  const missing = [...restoredComments.values()].filter((c) => c.eventId === eventId && !list.some((x) => x.id === c.id));
  if (missing.length > 0) qc.setQueryData<BoardComment[]>(commentsKey(eventId), [...list, ...missing].sort(byCreated));
}

function watchBoards(qc: QueryClient): void {
  if (watchingBoards) return;
  watchingBoards = true;
  qc.getQueryCache().subscribe((ev) => {
    if (restoredComments.size === 0 || ev.type !== 'updated' || ev.action.type !== 'success') return;
    const [root, eventId] = ev.query.queryKey;
    if (root === 'event-comments' && typeof eventId === 'string') injectRestored(qc, eventId);
  });
}

registerOutboxHandler('comment', {
  prepare: (item) => (item.body as QueuedComment).req,
  onSent(item, res) {
    const { eventId, req } = item.body as QueuedComment;
    restoredComments.delete(req.id);
    acceptServerComment(outboxQc, eventId, req.id, res);
    void outboxQc.invalidateQueries({ queryKey: eventKey(eventId) });
  },
  onRejected(item, error, immediate) {
    const { eventId, req } = item.body as QueuedComment;
    restoredComments.delete(req.id);
    setComment(outboxQc, eventId, req.id, (c) => ({ ...c, local: 'failed' }));
    if (!immediate) reportRejected({ kind: 'comment', eventId, error });
  },
  onRestore(item) {
    const { eventId, req, author, createdAt } = item.body as QueuedComment;
    restoredComments.set(req.id, {
      id: req.id,
      eventId,
      author,
      replyTo: req.replyTo ?? null,
      body: req.body,
      createdAt,
      updatedAt: createdAt,
      editedAt: null,
      deletedAt: null,
      local: 'queued',
    });
    watchBoards(outboxQc);
    injectRestored(outboxQc, eventId);
  },
  onReset: resetQueuedState,
});

/**
 * Posts a comment with a client UUID (idempotent on retry). The comment appears immediately;
 * offline it stays on the board marked "queued" and is re-sent when the network returns.
 */
export async function submitComment(
  eventId: string,
  req: CreateCommentRequest,
  author: CommentDTO['author'],
  qc: QueryClient = rootQueryClient,
): Promise<SubmitResult> {
  outboxQc = qc;
  const now = serverNow();
  const optimistic: BoardComment = {
    id: req.id,
    eventId,
    author,
    replyTo: req.replyTo ?? null,
    body: req.body,
    createdAt: now,
    updatedAt: now,
    editedAt: null,
    deletedAt: null,
    local: 'sending',
  };
  const existing = qc.getQueryData<BoardComment[]>(commentsKey(eventId));
  if (existing?.some((c) => c.id === req.id)) setComment(qc, eventId, req.id, (c) => ({ ...c, local: 'sending' }));
  else {
    qc.setQueryData<BoardComment[]>(commentsKey(eventId), (list) => [...(list ?? []), optimistic].sort(byCreated));
    patchEventEverywhere(qc, eventId, (ev) => ({ ...ev, commentCount: ev.commentCount + 1 }));
  }
  const body: QueuedComment = { eventId, req, author, createdAt: now };
  const outcome = await enqueue({ kind: 'comment', key: `comment:${req.id}`, method: 'POST', path: `/events/${encodeURIComponent(eventId)}/comments`, body });
  // 'rejected': the handler already marked the comment as failed.
  return submitResult(outcome, () => setComment(qc, eventId, req.id, (c) => ({ ...c, local: 'queued' })));
}

/** Removes a comment that was never accepted by the server (local "failed" entry). */
export function discardLocalComment(eventId: string, id: string, qc: QueryClient = rootQueryClient): void {
  retryQueue.delete(`comment:${id}`);
  restoredComments.delete(id);
  void removeQueued(`comment:${id}`).catch(() => undefined);
  setComment(qc, eventId, id, () => null);
  patchEventEverywhere(qc, eventId, (ev) => ({ ...ev, commentCount: Math.max(0, ev.commentCount - 1) }));
}

export function useEditComment(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: string }) => {
      const before = qc.getQueryData<BoardComment[]>(commentsKey(eventId))?.find((c) => c.id === id);
      const now = serverNow();
      setComment(qc, eventId, id, (c) => ({ ...c, body, editedAt: now, updatedAt: now, local: 'sending' }));
      try {
        const res = await api<unknown>(`/comments/${encodeURIComponent(id)}`, { method: 'PATCH', body: { body } });
        acceptServerComment(qc, eventId, id, res);
      } catch (e) {
        if (before) setComment(qc, eventId, id, () => before);
        throw e;
      }
    },
  });
}

export function useDeleteComment(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const before = qc.getQueryData<BoardComment[]>(commentsKey(eventId))?.find((c) => c.id === id);
      const now = serverNow();
      setComment(qc, eventId, id, (c) => ({ ...c, body: null, deletedAt: now, updatedAt: now, local: 'sending' }));
      try {
        const res = await api<unknown>(`/comments/${encodeURIComponent(id)}`, { method: 'DELETE' });
        acceptServerComment(qc, eventId, id, res);
        patchEventEverywhere(qc, eventId, (ev) => ({ ...ev, commentCount: Math.max(0, ev.commentCount - 1) }));
        void qc.invalidateQueries({ queryKey: eventKey(eventId) });
      } catch (e) {
        if (before) setComment(qc, eventId, id, () => before);
        throw e;
      }
    },
  });
}

/** Marks the board as read (comment_reads only advances server-side). Best effort: errors are ignored. */
export async function markCommentsRead(eventId: string, qc: QueryClient = rootQueryClient): Promise<void> {
  try {
    await api<unknown>(`/events/${encodeURIComponent(eventId)}/read`, { method: 'POST', body: {} });
    patchEventEverywhere(qc, eventId, (ev) => (ev.unreadCount === 0 ? ev : { ...ev, unreadCount: 0 }));
    dropNotification(qc, 'event', eventId);
  } catch {
    /* next visit marks it again */
  }
}

/* ─── Series & event editing (editors) ──────────────────────────────────── */

export function useSeries(id: string | null) {
  return useQuery({
    queryKey: seriesKey(id ?? ''),
    queryFn: () => api<SeriesDTO>(`/series/${encodeURIComponent(id!)}`),
    enabled: !!id,
    gcTime: 30 * 60_000,
  });
}

export type EventPatch = PatchEventRequest;
export type SeriesPatch = PatchSeriesRequest;

/**
 * After an editor write: lists are refetched (series changes touch many rows); an EventDTO / SeriesDTO
 * in the response is written straight into its cache.
 */
function afterCalendarWrite(qc: QueryClient, ids: { eventId?: string; seriesId?: string | null; res?: unknown } = {}): void {
  void qc.invalidateQueries({ queryKey: ['events'] });
  if (ids.eventId) {
    if (isEventDTO(ids.res, ids.eventId)) qc.setQueryData<EventDTO>(eventKey(ids.eventId), applyLocal(ids.res));
    else void qc.invalidateQueries({ queryKey: eventKey(ids.eventId) });
    void qc.invalidateQueries({ queryKey: headcountKey(ids.eventId) });
    void qc.invalidateQueries({ queryKey: rosterKey(ids.eventId) });
  } else {
    void qc.invalidateQueries({ queryKey: ['event'] });
  }
  if (ids.seriesId) {
    const r = ids.res as SeriesDTO | undefined;
    if (r && typeof r === 'object' && r.id === ids.seriesId && Array.isArray(r.weekdays)) qc.setQueryData<SeriesDTO>(seriesKey(ids.seriesId), r);
    else void qc.invalidateQueries({ queryKey: seriesKey(ids.seriesId) });
  }
}

export function useCalendarMutations() {
  const qc = useQueryClient();
  const createSeries = useMutation({
    mutationFn: (req: UpsertSeriesRequest) => api<SeriesDTO>('/series', { method: 'POST', body: req }),
    onSuccess: (res, req) => afterCalendarWrite(qc, { seriesId: req.id, res }),
  });
  const updateSeries = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SeriesPatch }) => api<SeriesDTO>(`/series/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    onSuccess: (res, v) => afterCalendarWrite(qc, { seriesId: v.id, res }),
  });
  const splitSeries = useMutation({
    mutationFn: ({ id, req }: { id: string; req: SplitSeriesRequest }) =>
      api<SeriesDTO>(`/series/${encodeURIComponent(id)}/split`, { method: 'POST', body: req }),
    onSuccess: (res, v) => {
      afterCalendarWrite(qc, { seriesId: v.id });
      afterCalendarWrite(qc, { seriesId: v.req.newSeriesId, res });
    },
  });
  const deleteSeries = useMutation({
    mutationFn: (id: string) => api<unknown>(`/series/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_r, id) => afterCalendarWrite(qc, { seriesId: id }),
  });
  const createEvent = useMutation({
    mutationFn: (req: UpsertEventRequest) => api<EventDTO>('/events', { method: 'POST', body: req }),
    onSuccess: (res, req) => afterCalendarWrite(qc, { eventId: req.id, res }),
  });
  const patchEvent = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: EventPatch }) => api<EventDTO>(`/events/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    onSuccess: (res, v) => afterCalendarWrite(qc, { eventId: v.id, res }),
  });
  const cancelEvent = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      api<EventDTO>(`/events/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: reason ? { reason } : {} }),
    onSuccess: (res, v) => afterCalendarWrite(qc, { eventId: v.id, res }),
  });
  const uncancelEvent = useMutation({
    mutationFn: (id: string) => api<EventDTO>(`/events/${encodeURIComponent(id)}/uncancel`, { method: 'POST', body: {} }),
    onSuccess: (res, id) => afterCalendarWrite(qc, { eventId: id, res }),
  });
  /** Deletes an event; the server cancels it instead when it already has RSVPs or comments. */
  const deleteEvent = useMutation({
    mutationFn: (id: string) => api<DeleteEventResponse>(`/events/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (res, id) => {
      if (res?.status === 'deleted') qc.removeQueries({ queryKey: eventKey(id), exact: true });
      afterCalendarWrite(qc, { eventId: id });
    },
  });
  return { createSeries, updateSeries, splitSeries, deleteSeries, createEvent, patchEvent, cancelEvent, uncancelEvent, deleteEvent };
}

/* ─── Collections (setlist picker / link) ───────────────────────────────── */

/** Same key and fetcher as the library screens (api/library.ts) so both share one cache entry. */
export function useCollections(enabled = true) {
  return useQuery<CollectionDTO[]>({
    queryKey: COLLECTIONS_KEY,
    queryFn: ({ signal }) => fetchCollections(signal),
    meta: { persist: true },
    enabled,
  });
}

/* ─── Misc ──────────────────────────────────────────────────────────────── */

/** Re-renders every `ms` with the estimated server time (check-in windows, relative times). */
export function useServerNow(ms = 30_000): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const tick = () => setNow(serverNow());
    const id = setInterval(tick, ms);
    const onVis = () => document.visibilityState === 'visible' && tick();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ms]);
  return now;
}
